/**
 * Admin Payment Slips Management
 * Dashboard for reviewing and approving/rejecting payment slips
 */

import { useState } from 'react';
import {
  usePendingSlips,
  useSlipStatistics,
  useApproveSlip,
  useRejectSlip,
  useMarkSlipUnderReview,
} from '../../hooks/usePaymentSlips';
import type { PaymentSlip } from '../../types/payment-slip';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle, Clock, XCircle, Eye, FileCheck } from 'lucide-react';
import { toast } from 'sonner';

export function AdminPaymentSlips() {
  const [selectedSlip, setSelectedSlip] = useState<PaymentSlip | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed' | 'failed'>('pending');
  const pageSize = 20;

  // Queries
  const { data: slipsData, isLoading, error } = usePendingSlips({
    limit: pageSize,
    offset: page * pageSize,
    sortBy: 'submitted_at',
    sortOrder: 'DESC',
    status: statusFilter,
  });

  const { data: stats } = useSlipStatistics();
  const pendingCount = stats?.summary?.pendingCount || 0;
  const hasPendingItems = pendingCount > 0;

  // Mutations
  const approveSlip = useApproveSlip();
  const rejectSlip = useRejectSlip();
  const markUnderReview = useMarkSlipUnderReview();

  const handleViewDetails = (slip: PaymentSlip) => {
    setSelectedSlip(slip);
    setShowDetailModal(true);
  };

  const handleMarkUnderReview = async (slipId: string) => {
    try {
      await markUnderReview.mutateAsync(slipId);
      toast.success('มาร์กเป็น "กำลังตรวจสอบ" แล้ว');
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message);
    }
  };

  const handleApprove = async () => {
    if (!selectedSlip) return;

    try {
      await approveSlip.mutateAsync({
        slipId: selectedSlip.id,
        request: { adminNotes },
      });
      toast.success('อนุมัติสำเร็จ - เครดิตถูกเติมให้ลูกค้าแล้ว');
      setShowDetailModal(false);
      setSelectedSlip(null);
      setAdminNotes('');
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      toast.error('ต้องระบุเหตุผลในการปฏิเสธ');
      return;
    }

    if (!selectedSlip) return;

    try {
      await rejectSlip.mutateAsync({
        slipId: selectedSlip.id,
        request: {
          rejectionReason,
          adminNotes,
        },
      });
      toast.success('ปฏิเสธสำเร็จ - ลูกค้าได้รับการแจ้งเตือนแล้ว');
      setShowRejectModal(false);
      setShowDetailModal(false);
      setSelectedSlip(null);
      setRejectionReason('');
      setAdminNotes('');
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to check if transaction is package purchase
  const isPackagePurchase = (slip: PaymentSlip) => {
    return slip.transaction_type === 'package_purchase' || slip.reference_number?.startsWith('PKG_');
  };

  const getStatusBadge = (status: string) => {
    const config = {
      created: { variant: 'secondary' as const, icon: AlertCircle, label: 'รอยืนยัน', className: 'bg-gray-500' },
      awaiting_slip: { variant: 'secondary' as const, icon: Clock, label: 'รอสลิป', className: 'bg-yellow-500' },
      submitted: { variant: 'secondary' as const, icon: AlertCircle, label: 'รอตรวจสอบ', className: undefined },
      slip_submitted: { variant: 'secondary' as const, icon: AlertCircle, label: 'ส่งสลิปแล้ว', className: 'bg-blue-400' },
      under_review: { variant: 'default' as const, icon: Clock, label: 'กำลังตรวจสอบ', className: undefined },
      approved: { variant: 'default' as const, icon: CheckCircle, label: 'อนุมัติแล้ว', className: 'bg-green-500' },
      completed: { variant: 'default' as const, icon: CheckCircle, label: 'อนุมัติแล้ว', className: 'bg-green-500' },
      rejected: { variant: 'destructive' as const, icon: XCircle, label: 'ปฏิเสธแล้ว', className: 'bg-red-600 text-white' },
      failed: { variant: 'destructive' as const, icon: XCircle, label: 'ปฏิเสธแล้ว', className: 'bg-red-600 text-white' },
    };

    const { variant, icon: Icon, label, className } = config[status as keyof typeof config] || config.submitted;

    return (
      <Badge variant={variant} className={className}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg text-muted-foreground">กำลังโหลด...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-destructive">เกิดข้อผิดพลาด: {(error as Error).message}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">จัดการสลิปการชำระเงิน</h1>
        <p className="text-muted-foreground">ตรวจสอบและอนุมัติ/ปฏิเสธสลิปการโอนเงินจากลูกค้า</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card 
            className={`cursor-pointer transition-all hover:ring-2 ${statusFilter === 'pending' ? 'ring-2 ring-yellow-500' : ''} ${hasPendingItems ? 'bg-red-50 border-red-200 shadow-md hover:shadow-lg' : ''}`}
            onClick={() => {
              setStatusFilter('pending');
              setPage(0);
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className={`text-sm font-medium ${hasPendingItems ? 'text-red-700' : ''}`}>รอตรวจสอบ</CardTitle>
              <AlertCircle className={`h-4 w-4 ${hasPendingItems ? 'text-red-600' : 'text-muted-foreground'}`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${hasPendingItems ? 'text-red-700' : 'text-yellow-600'}`}>{pendingCount}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.summary?.totalCredits ? `${stats.summary.totalCredits.toLocaleString()} เครดิต` : ''}
              </p>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:ring-2 ${statusFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => {
              setStatusFilter('all');
              setPage(0);
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">ทั้งหมด</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.summary?.totalTransactions || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.summary?.totalAmount ? `฿${stats.summary.totalAmount.toLocaleString()}` : ''}
              </p>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:ring-2 ${statusFilter === 'completed' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => {
              setStatusFilter('completed');
              setPage(0);
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">อนุมัติแล้ว</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.summary?.completedCount || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.byStatus?.completed ? `${stats.byStatus.completed.totalCredits.toLocaleString()} เครดิต` : ''}
              </p>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:ring-2 ${statusFilter === 'failed' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => {
              setStatusFilter('failed');
              setPage(0);
            }}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">ปฏิเสธแล้ว</CardTitle>
              <XCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.summary?.failedCount || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.byStatus?.failed ? `${stats.byStatus.failed.totalCredits.toLocaleString()} เครดิต` : ''}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Slips Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {statusFilter === 'all' && 'รายการทั้งหมด'}
            {statusFilter === 'pending' && 'รายการสลิปรอดำเนินการ'}
            {statusFilter === 'completed' && 'รายการอนุมัติแล้ว'}
            {statusFilter === 'failed' && 'รายการปฏิเสธแล้ว'}
          </CardTitle>
          <CardDescription>
            แสดง {slipsData?.slips.length || 0} รายการ จากทั้งหมด {slipsData?.pagination.total || 0} รายการ
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>เวลา</TableHead>
                <TableHead>หมายเลขอ้างอิง</TableHead>
                <TableHead>ลูกค้า</TableHead>
                <TableHead>ประเภท</TableHead>
                <TableHead>จำนวนเงิน</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead className="text-right">การดำเนินการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {slipsData?.slips.map((slip) => (
                <TableRow key={slip.id}>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(slip.submitted_at)}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{slip.reference_number}</div>
                    <div className="text-xs text-muted-foreground">{slip.payment_type}</div>
                  </TableCell>
                  <TableCell>
                    <div>{slip.business_name || slip.customer_email}</div>
                    <div className="text-xs text-muted-foreground">
                      {slip.line_display_name || slip.phone}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">
                      {isPackagePurchase(slip) ? (
                        <span className="text-blue-600">ซื้อแพ็กเกจ</span>
                      ) : (
                        <span className="text-green-600">เติมเครดิต</span>
                      )}
                    </div>
                    {slip.package_name && (
                      <div className="text-xs text-muted-foreground">{slip.package_name}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{formatCurrency(slip.stated_amount)}</div>
                    {slip.credit_amount && (
                      <div className="text-xs text-muted-foreground">{slip.credit_amount} เครดิต</div>
                    )}
                  </TableCell>
                  <TableCell>{getStatusBadge(slip.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(slip)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        ดูรายละเอียด
                      </Button>
                      {slip.status === 'submitted' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMarkUnderReview(slip.id)}
                        >
                          <FileCheck className="h-4 w-4 mr-1" />
                          เริ่มตรวจสอบ
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {slipsData && slipsData.pagination.total > pageSize && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                แสดง {page * pageSize + 1} ถึง{' '}
                {Math.min((page + 1) * pageSize, slipsData.pagination.total)} จาก{' '}
                {slipsData.pagination.total} รายการ
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  ก่อนหน้า
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!slipsData.pagination.hasMore}
                >
                  ถัดไป
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>รายละเอียดสลิป</DialogTitle>
            <DialogDescription>
              หมายเลขอ้างอิง: {selectedSlip?.reference_number}
            </DialogDescription>
          </DialogHeader>

          {selectedSlip && (
            <div className="grid gap-6 md:grid-cols-2">
              {/* Slip Image */}
              <div className="space-y-3">
                <h3 className="font-semibold">ภาพสลิป</h3>
                {selectedSlip.slip_image_url || selectedSlip.slip_image_path ? (
                  <img
                    src={
                      selectedSlip.slip_image_url ||
                      `/uploads/payment-slips/${selectedSlip.slip_image_path}`
                    }
                    alt="Payment Slip"
                    className="w-full rounded-lg border"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage not available%3C/text%3E%3C/svg%3E';
                    }}
                  />
                ) : (
                  <div className="w-full h-64 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
                    <AlertCircle className="h-12 w-12 text-amber-500 mb-3" />
                    <p className="text-sm font-medium text-gray-900 mb-2">การยืนยันแบบ Manual</p>
                    <p className="text-xs text-gray-600">ลูกค้ายืนยันการชำระเงินผ่านช่องทางอื่น</p>
                    <p className="text-xs text-gray-500 mt-1">(โทรศัพท์ / LINE Chat / Facebook)</p>
                  </div>
                )}
              </div>

              {/* Slip Details */}
              <div className="space-y-6">
                <div className="space-y-3">
                  <h3 className="font-semibold">{isPackagePurchase(selectedSlip) ? 'ข้อมูลการซื้อแพ็กเกจ' : 'ข้อมูลการโอนเงิน'}</h3>
                  <div className="grid gap-2 text-sm">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-muted-foreground">หมายเลขอ้างอิง:</div>
                      <div className="col-span-2 font-medium">{selectedSlip.reference_number}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-muted-foreground">จำนวนเงิน:</div>
                      <div className="col-span-2 font-medium text-lg">
                        {formatCurrency(selectedSlip.stated_amount)}
                      </div>
                    </div>
                    {isPackagePurchase(selectedSlip) ? (
                      <>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="text-muted-foreground">แพ็กเกจ:</div>
                          <div className="col-span-2 font-medium">{selectedSlip.package_name || 'Enterprise Plan'}</div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="text-muted-foreground">เครดิตรวมในแพ็กเกจ:</div>
                          <div className="col-span-2 font-medium">{selectedSlip.credit_amount} เครดิต/เดือน</div>
                        </div>
                      </>
                    ) : (
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-muted-foreground">เครดิตที่จะได้รับ:</div>
                        <div className="col-span-2 font-medium">{selectedSlip.credit_amount} เครดิต</div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-muted-foreground">ประเภท:</div>
                      <div className="col-span-2">{isPackagePurchase(selectedSlip) ? 'การซื้อแพ็กเกจ' : 'การเติมเครดิต'}</div>
                    </div>
                    {selectedSlip.package_code && (
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-muted-foreground">แพ็คเกจ:</div>
                        <div className="col-span-2">{selectedSlip.package_code}</div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-muted-foreground">เวลาส่ง:</div>
                      <div className="col-span-2">{formatDate(selectedSlip.submitted_at)}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-muted-foreground">สถานะ:</div>
                      <div className="col-span-2">{getStatusBadge(selectedSlip.status)}</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold">ข้อมูลลูกค้า</h3>
                  <div className="grid gap-2 text-sm">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-muted-foreground">ชื่อธุรกิจ:</div>
                      <div className="col-span-2">{selectedSlip.business_name || '-'}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-muted-foreground">อีเมล:</div>
                      <div className="col-span-2">{selectedSlip.customer_email}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-muted-foreground">เบอร์โทร:</div>
                      <div className="col-span-2">{selectedSlip.phone || '-'}</div>
                    </div>
                    {selectedSlip.line_display_name && (
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-muted-foreground">LINE:</div>
                        <div className="col-span-2">{selectedSlip.line_display_name}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Admin Notes */}
                {['created', 'awaiting_slip', 'submitted', 'slip_submitted', 'under_review'].includes(selectedSlip.status) && (
                  <div className="space-y-2">
                    <Label>หมายเหตุแอดมิน (ไม่บังคับ)</Label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder={selectedSlip.slip_image_url || selectedSlip.slip_image_path ? "บันทึกหมายเหตุภายใน..." : "ระบุช่องทางการยืนยัน เช่น 'ยืนยันผ่านโทรศัพท์' หรือ 'ตรวจสอบผ่าน LINE Chat'"}
                      rows={3}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedSlip &&
            ['created', 'awaiting_slip', 'submitted', 'slip_submitted', 'under_review'].includes(selectedSlip.status) && (
              <DialogFooter className="mt-6 pt-4 border-t">
                <Button
                  variant="destructive"
                  onClick={() => setShowRejectModal(true)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  ปฏิเสธ
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={approveSlip.isPending}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {approveSlip.isPending ? 'กำลังประมวลผล...' : 
                    (selectedSlip && isPackagePurchase(selectedSlip) ? 'อนุมัติการซื้อแพ็กเกจ' : 'อนุมัติและเติมเครดิต')
                  }
                </Button>
              </DialogFooter>
            )}
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ปฏิเสธการชำระเงิน</DialogTitle>
            <DialogDescription>กรุณาระบุเหตุผลในการปฏิเสธ ลูกค้าจะได้รับการแจ้งเตือน</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mb-6">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">
                เหตุผลในการปฏิเสธ <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="เช่น: ภาพไม่ชัด, จำนวนเงินไม่ตรง, หมายเลขอ้างอิงไม่ถูกต้อง..."
                rows={4}
                required
              />
            </div>
          </div>

          <DialogFooter className="mt-8 pt-4 border-t gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectModal(false);
                setRejectionReason('');
              }}
              className="bg-white hover:bg-gray-100 text-gray-700 border-gray-300"
            >
              ยกเลิก
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectSubmit}
              disabled={rejectSlip.isPending || !rejectionReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {rejectSlip.isPending ? 'กำลังประมวลผล...' : 'ยืนยันการปฏิเสธ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminPaymentSlips;
